import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
  Cell,
} from "recharts";

const fmtInt = (n) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Number(n || 0));

const truncate = (s = "", n = 18) => (s.length > n ? s.slice(0, n - 1) + "…" : s);

function pct(a, b) {
  const A = Number(a || 0);
  const B = Number(b || 0);
  if (!B) return 0;
  return (A / B) * 100;
}

const RANK_COLORS = [
  "#FF6A88", // #1
  "#8753FF", // #2
  "#5A8DEE", // #3
  "#34D399", // #4
  "#FBBF24", // #5
  "#60A5FA",
  "#A78BFA",
  "#FB7185",
  "#22C55E",
  "#38BDF8",
];

function CustomTooltip({ active, payload, total, valueLabel }) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  const value = Number(p?.value || 0);
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg px-3 py-2">
      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
        {p?.name}
      </div>
      <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
        {valueLabel || "Valeur"} : <span className="font-semibold">{fmtInt(value)}</span>
      </div>
      {total ? (
        <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
          Part : <span className="font-semibold">{pct(value, total).toFixed(1)}%</span>
        </div>
      ) : null}
    </div>
  );
}

export function TopVisitsBarChart({ data = [], loading = false, valueLabel = "", total = 0 }) {
  if (loading) {
    return <div className="h-[280px] rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />;
  }

  if (!data?.length) {
    return (
      <div className="h-[280px] rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-center text-slate-500">
        Aucune donnée
      </div>
    );
  }

  const chartData = data.map((d) => ({
    ...d,
    short: truncate(d.name, 18),
  }));

  return (
    <div className="h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 8, right: 18, left: 8, bottom: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.35} />
          <XAxis
            type="number"
            tick={{ fontSize: 12 }}
            tickFormatter={(v) => fmtInt(v)}
          />
          <YAxis
            type="category"
            dataKey="short"
            width={110}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip total={total} valueLabel={valueLabel} />} />
          <Bar dataKey="value" radius={[10, 10, 10, 10]} barSize={16}>
            {chartData.map((_, idx) => (
              <Cell key={idx} fill={RANK_COLORS[idx] || "#94A3B8"} />
            ))}
            <LabelList
              dataKey="value"
              position="right"
              formatter={(v) => fmtInt(v)}
              style={{ fontSize: 12, fontWeight: 600, fill: "currentColor" }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default TopVisitsBarChart;
